import { toast } from 'react-toastify';
import { DataRoomAccessStatus } from 'shared';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { getTranslation } from '../../utils/get-translation.util';
import { useAppMutation } from '../use-app-mutation.hook';
import { useInvalidateQueries } from '../use-invalidate-queries.hook';
import { useSelectedCompany } from '../use-selected-company.hook';
import { accessActionCopy } from './access-action-copy.const';
import { DATA_ROOM_ACCESS_INVALIDATION_KEYS } from './access-invalidation.const';
import { DataRoomAccessAction, DataRoomAccessRow } from './space.types';

type AccessActionVariables = {
  accessId: string;
  action: DataRoomAccessAction;
  id: string;
  // The status the row held when the action was chosen. Revoking reads differently for somebody
  // who never got in, and the confirmation the Admin answered says so — the toast has to agree.
  status: DataRoomAccessStatus;
};

/** Revoke / Grant access / Resend — applied immediately, never batched into Save. */
export const useAccessAction = () => {
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id ?? '';
  const { invalidateQuery } = useInvalidateQueries(...DATA_ROOM_ACCESS_INVALIDATION_KEYS);

  const { isPending, mutate: applyAccessAction } = useAppMutation<
    DataRoomAccessRow,
    unknown,
    AccessActionVariables
  >([QueryKey.DATA_ROOM_ACCESS_ACTION, companyId], {
    mutationFn: ({ accessId, action, id }) =>
      ApiService.post(
        { endpoint: BackendRoute.DATA_ROOMS, routePath: [id, 'access', accessId, 'actions'] },
        { body: { action } },
      ),
    onSuccess: (_row, { action, status }) => {
      invalidateQuery();
      toast.success(getTranslation(accessActionCopy({ action, status }).successMessage));
    },
  });

  return { applyAccessAction, isPending };
};
